import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { EdgeFunctionName } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

interface EditRoastParams {
  roastId: string;
  site_name?: string;
  site_description?: string;
  site_logo_url?: string;
  category?: string;
  pricing_model?: string;
  tags?: string[];
  socials?: Record<string, string>;
  contact_email?: string;
}

interface EditRoastResponse {
  roast_id: string;
  message: string;
}

export const useEditRoastMutation = () => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const { id: userId } = useCurrentUser();
  const { toastSuccess, toastError } = useToastWrapper();

  return useMutation<EditRoastResponse, string, EditRoastParams>({
    mutationFn: supabaseMutationFn(({ roastId, ...fields }) =>
      supabase.functions.invoke<EditRoastResponse>(EdgeFunctionName.Roast, {
        body: { type: 'edit', roast_id: roastId, ...fields },
      })
    ),
    onSuccess: (_, { roastId }) => {
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetRoasts, userId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetAllRoasts] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetRoastDetail, roastId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetProfileRoasts, userId] });
      toastSuccess('Roast updated');
    },
    onError: (error) => {
      toastError(error || 'Failed to update roast');
    },
  });
};
